
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Agent, AgentProduct } from '@/types/database.types';

export async function getAgents() {
  const { data, error } = await supabase
    .from('agents')
    .select(`
      *,
      agent_products(*),
      agent_categories(*)
    `)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching agents:', error);
    throw error;
  }

  return data;
}

export async function getAgentById(id: string) {
  const { data, error } = await supabase
    .from('agents')
    .select(`
      *,
      agent_products(*),
      agent_categories(*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    console.error(`Error fetching agent with id ${id}:`, error);
    throw error;
  }

  return data;
}

export async function createAgent(agent: Omit<Agent, 'created_at' | 'id' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('agents')
    .insert({
      name: agent.name,
      phone_number: agent.phone_number,
      email: agent.email,
      address: agent.address,
      city: agent.city,
      region: agent.region,
      tax_number: agent.tax_number,
      bank_name: agent.bank_name,
      bank_account: agent.bank_account,
      bank_branch: agent.bank_branch,
      status: agent.status || 'active',
      payment_term_type: agent.payment_term_type,
      payment_term_days: agent.payment_term_days,
      supports_damage_return: agent.supports_damage_return,
      damage_return_percentage: agent.damage_return_percentage
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating agent:', error);
    throw error;
  }

  return data;
}

export async function addAgentProduct(product: Omit<AgentProduct, 'created_at' | 'id'>) {
  const { data, error } = await supabase
    .from('agent_products')
    .insert({
      agent_id: product.agent_id,
      product_name: product.product_name
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding agent product:', error);
    throw error;
  }

  return data;
}

export async function updateAgent(id: string, agent: Partial<Agent>) {
  const { data, error } = await supabase
    .from('agents')
    .update(agent)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error(`Error updating agent with id ${id}:`, error);
    throw error;
  }

  return data;
}

export async function deleteAgent(id: string) {
  const { error } = await supabase
    .from('agents')
    .delete()
    .eq('id', id);

  if (error) {
    console.error(`Error deleting agent with id ${id}:`, error);
    throw error;
  }

  return true;
}
